import React, { useState, useCallback } from 'react';
import {
  Dialog,
  Divider,
  Stack,
  IconButton,
  Typography,
  FormControlLabel,
  Paper,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { generateClient } from 'aws-amplify/api';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';
import { Schema } from '../../../../../../../../amplify/data/resource';
import { useAuth } from '../../../../../../../utils/context/useAuthContext';
import awsConfig from '../../../../../../../amplify_outputs.json';

import SendButton from '../../../../../../../components/custom/SendButton';
import { Switch } from '../../../../../../../components/custom/Switch';
import Button from '../../../../../../../components/custom/Button';

interface TransferFileProps {
  fileId: string;
  open: boolean;
  onClose: () => void;
}

const apiClient = generateClient<Schema>();
const S3_BUCKET_NAME = awsConfig.storage.bucket_name;

// Helper function to download file from response
const downloadFileFromResponse = (response: any, defaultFileName: string) => {
  try {
    let fileData, fileName, mimeType;
    
    console.log('Raw response received:', response);
    
    // Handle different response formats
    if (response?.data) {
      // If response.data is a string, try to parse it
      if (typeof response.data === 'string') {
        try {
          const parsedData = JSON.parse(response.data);
          console.log('Parsed response data:', parsedData);
          
          if (parsedData.body) {
            // Lambda response structure: {statusCode, headers, body, isBase64Encoded}
            fileData = parsedData.body; // Already base64 from lambda
            
            // Extract filename from Content-Disposition header
            if (parsedData.headers && parsedData.headers['Content-Disposition']) {
              const contentDisposition = parsedData.headers['Content-Disposition'];
              console.log('Content-Disposition header:', contentDisposition);
              
              // Parse filename from Content-Disposition: attachment; filename="filename.ext"
              const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (filenameMatch && filenameMatch[1]) {
                fileName = filenameMatch[1].replace(/['"]/g, '');
                console.log('Extracted filename from Content-Disposition:', fileName);
              }
            }
            
            // Get content type from headers
            mimeType = parsedData.headers?.['Content-Type'] || 'application/octet-stream';
          }
        } catch (parseError) {
          console.error('Error parsing response data:', parseError);
          return;
        }
      } else if (response.data.body) {
        // Direct access to body (fallback for other response formats)
        fileData = response.data.body;
        fileName = response.data.fileName || response.data.filename || response.data.name;
        mimeType = response.data.mimeType || response.data.contentType || response.data.type || 'application/octet-stream';
      }
    }

    if (!fileData) {
      console.error('No file data found in response');
      return;
    }

    // Use server filename if available, otherwise use default
    const finalFileName = fileName || defaultFileName;
    console.log('Final filename to be used:', finalFileName);

    // Convert base64 string to bytes
    const byteCharacters = atob(fileData);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Create blob and download
    const blob = new Blob([byteArray], { type: mimeType });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = finalFileName;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log(`File downloaded successfully: ${finalFileName}`);
    
  } catch (error) {
    console.error('Error downloading file:', error);
  }
};

const TransferFile: React.FC<TransferFileProps> = ({
  fileId,
  open,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [previousGUIDS, setPreviousGUIDS] = useState(false);
  const [previousMoneyGUIDS, setPreviousMoneyGUIDS] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const userId = user?.userId;

  // Handle drag events
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  // Handle drop event
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  }, []);

  // Handle file input change
  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  }, []);

  // Remove selected file
  const removeFile = useCallback(() => {
    setSelectedFile(null);
  }, []);

  const onSubmit = async () => {
    if (!selectedFile) {
      enqueueSnackbar('אנא בחר קובץ להעלאה', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      if (!userId) {
        throw new Error('User ID is missing');
      }

      // First upload file to S3
      console.log('[onSubmit] Uploading file to S3...');
      const uploadedFilePath = await uploadFileToS3(selectedFile);
      console.log('[onSubmit] File uploaded to:', uploadedFilePath);

      // Call mislakaTransferReport API with uploaded file path
      const mislakaResponse = await apiClient.queries.mislakaTransferReport({
        fileId: fileId,
        path: uploadedFilePath, // Use uploaded file path
        previousGUIDS: previousGUIDS,
        previousMoneyGUIDS: previousMoneyGUIDS
      });

      // Check response status
      if (mislakaResponse?.data) {
        let parsedResponse;
        try {
          parsedResponse = typeof mislakaResponse.data === 'string' 
            ? JSON.parse(mislakaResponse.data) 
            : mislakaResponse.data;
        } catch (parseError) {
          throw new Error('Failed to parse response from server');
        }

        if (parsedResponse.statusCode && parsedResponse.statusCode !== 200) {
          // Extract error message
          let errorMessage = 'Unknown error occurred';
          if (parsedResponse.body) {
            try {
              const errorBody = typeof parsedResponse.body === 'string' 
                ? JSON.parse(parsedResponse.body) 
                : parsedResponse.body;
              errorMessage = errorBody.message || errorMessage;
            } catch {
              errorMessage = parsedResponse.body;
            }
          }
          throw new Error(errorMessage);
        }

        // Download file if response is successful
        downloadFileFromResponse(mislakaResponse, `transferReport_${fileId}.txt`);
      }

      setLoading(false);
      enqueueSnackbar('File processed and sent via SFTP successfully', {
        variant: 'success',
      });

    } catch (error) {
      console.error('Failed to submit:', error);
      setLoading(false);
      enqueueSnackbar('Failed to process file. Please check your data and try again.', { 
        variant: 'error' 
      });
    }
  };

  const onDownload = async () => {
    if (!selectedFile) {
      enqueueSnackbar('אנא בחר קובץ להעלאה', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      if (!userId) {
        throw new Error('User ID is missing');
      }

      // First upload file to S3
      console.log('[onDownload] Uploading file to S3...');
      const uploadedFilePath = await uploadFileToS3(selectedFile);
      console.log('[onDownload] File uploaded to:', uploadedFilePath);

      // Call mislakaTransferReport API with download: true
      const mislakaResponse = await apiClient.queries.mislakaTransferReport({
        fileId: fileId,
        path: uploadedFilePath, // Use uploaded file path
        download: true,
        previousGUIDS: previousGUIDS,
        previousMoneyGUIDS: previousMoneyGUIDS
      });

      // Check response status
      if (mislakaResponse?.data) {
        let parsedResponse;
        try {
          parsedResponse = typeof mislakaResponse.data === 'string' 
            ? JSON.parse(mislakaResponse.data) 
            : mislakaResponse.data;
        } catch (parseError) {
          throw new Error('Failed to parse response from server');
        }

        if (parsedResponse.statusCode && parsedResponse.statusCode !== 200) {
          // Extract error message
          let errorMessage = 'Unknown error occurred';
          if (parsedResponse.body) {
            try {
              const errorBody = typeof parsedResponse.body === 'string' 
                ? JSON.parse(parsedResponse.body) 
                : parsedResponse.body;
              errorMessage = errorBody.message || errorMessage;
            } catch {
              errorMessage = parsedResponse.body;
            }
          }
          throw new Error(errorMessage);
        }

        // Download file if response is successful
        downloadFileFromResponse(mislakaResponse, `transferReport_download_${fileId}.txt`);
      }

      setLoading(false);
      enqueueSnackbar('File downloaded successfully', {
        variant: 'success',
      });

    } catch (error) {
      console.error('Failed to download:', error);
      setLoading(false);
      enqueueSnackbar('Failed to download file. Please check your data and try again.', { 
        variant: 'error' 
      });
    }
  };



  // Helper function to convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result && typeof reader.result === 'string') {
          // Remove data URL prefix (data:type;base64,)
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        } else {
          reject(new Error('Failed to convert file to base64'));
        }
      };
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsDataURL(file);
    });
  };

  // Function to upload file to S3
  const uploadFileToS3 = async (file: File): Promise<string> => {
    try {
      const base64File = await fileToBase64(file);
      const uuid = uuidv4();
      const fileName = file.name;
      const filePath = `files/${fileId}_${uuid}_${fileName}`;

      console.log(`[uploadFileToS3] Uploading file to path: ${filePath}`);

      const uploadResponse = await apiClient.queries.uploadFile({
        file: base64File,
        path: filePath,
        bucketName: S3_BUCKET_NAME,
      });

      if (uploadResponse?.data) {
        // First JSON parse - get the outer response
        const outerResponse = typeof uploadResponse.data === 'string' 
          ? JSON.parse(uploadResponse.data) 
          : uploadResponse.data;

        if (outerResponse.statusCode === 200) {
          // Second level - get the body data
          const responseBody = outerResponse.body;
          
          if (responseBody && responseBody.uploadedPath) {
            console.log(`[uploadFileToS3] File uploaded successfully to: ${responseBody.uploadedPath}`);
            return responseBody.uploadedPath;
          } else {
            throw new Error('Upload succeeded but no uploadedPath returned');
          }
        } else {
          // Handle HTTP error status
          const errorMessage = outerResponse.body?.message || outerResponse.body?.error || 'Upload failed';
          throw new Error(errorMessage);
        }
      } else {
        throw new Error('No response from upload service');
      }
    } catch (error) {
      console.error('[uploadFileToS3] Error uploading file:', error);
      throw new Error(`Failed to upload file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '500px',
          borderRadius: '35px',
        },
      }}
    >
      <Stack
        alignItems="center"
        spacing={3}
        sx={{
          '& .MuiDialog-paper': {
            padding: '60px',
            boxSizing: 'border-box',
            position: 'relative',
            overflow: 'hidden',
          },
          px: '40px',
          pt: '40px',
          pb: '70px',
        }}
      >
        <Stack direction="row" justifyContent="space-between" width="100%">
          <IconButton onClick={onClose}>
            <Icon icon="material-symbols:close-rounded" />
          </IconButton>
          <Stack direction="row" sx={{ alignItems: 'flex-end' }}>
            <Typography
              fontWeight={'700'}
              fontSize={'16px'}
              fontFamily={'AlmoniDL'}
              color={'#2A3447'}
            >
              העלה קובץ לשליחה למסלקה
            </Typography>
          </Stack>
        </Stack>

        {/* File Upload Section */}
        <Paper
          sx={{
            width: '100%',
            minHeight: '200px',
            border: `2px dashed ${dragActive ? '#94C729' : '#ccc'}`,
            borderRadius: '12px',
            backgroundColor: dragActive ? '#f0f8e8' : '#fafafa',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            position: 'relative',
            '&:hover': {
              borderColor: '#94C729',
              backgroundColor: '#f0f8e8',
            }
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => document.getElementById('fileInput')?.click()}
        >
          <input
            id="fileInput"
            type="file"
            onChange={handleFileInput}
            style={{ display: 'none' }}
            accept=".xml,.txt,.dat"
          />
          
          {selectedFile ? (
            <Stack alignItems="center" spacing={2}>
              <Icon 
                icon="mdi:file-check" 
                width={48} 
                height={48} 
                color="#94C729" 
              />
              <Typography
                fontFamily="AlmoniDL"
                fontWeight="700"
                fontSize="16px"
                color="#94C729"
              >
                {selectedFile.name}
              </Typography>
              <Typography
                fontFamily="Montserrat"
                fontSize="12px"
                color="#666"
              >
                {(selectedFile.size / 1024).toFixed(2)} KB
              </Typography>
              <IconButton 
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile();
                }}
                sx={{ 
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: '#fff',
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
              >
                <Icon icon="mdi:close" width={20} height={20} />
              </IconButton>
            </Stack>
          ) : (
            <Stack alignItems="center" spacing={2}>
              <Icon 
                icon="mdi:cloud-upload" 
                width={48} 
                height={48} 
                color={dragActive ? '#94C729' : '#ccc'} 
              />
              <Typography
                fontFamily="AlmoniDL"
                fontWeight="700"
                fontSize="16px"
                color="#666"
                textAlign="center"
              >
                גרור קובץ לכאן או לחץ לבחירה
              </Typography>
              <Typography
                fontFamily="Montserrat"
                fontSize="12px"
                color="#999"
                textAlign="center"
              >
                נתמכים: XML, TXT, DAT
              </Typography>
            </Stack>
          )}
        </Paper>

        {/* GUID Settings */}
        <Divider sx={{ width: '100%' }} />
        <Stack spacing={2} sx={{ width: '100%' }}>
          <FormControlLabel
                         control={
               <Switch
                 checked={previousGUIDS}
                 onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPreviousGUIDS(e.target.checked)}
               />
             }
            label={
              <Typography
                fontWeight="600"
                fontSize="14px"
                fontFamily="AlmoniDL"
                color="#2A3447"
                dir="rtl"
              >
                {previousGUIDS ? 'השתמש ב-GUID קודמים' : 'צור GUID חדשים'}
              </Typography>
            }
            labelPlacement="start"
            sx={{ justifyContent: 'space-between', margin: 0 }}
          />

          <FormControlLabel
                         control={
               <Switch
                 checked={previousMoneyGUIDS}
                 onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPreviousMoneyGUIDS(e.target.checked)}
               />
             }
            label={
              <Typography
                fontWeight="600"
                fontSize="14px"
                fontFamily="AlmoniDL"
                color="#2A3447"
                dir="rtl"
              >
                {previousMoneyGUIDS ? 'השתמש ב-GUID כספים קודמים' : 'צור GUID כספים חדשים'}
              </Typography>
            }
            labelPlacement="start"
            sx={{ justifyContent: 'space-between', margin: 0 }}
          />
        </Stack>

        {/* Buttons */}
        <Divider sx={{ width: '100%' }} />
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: '100%' }}
        >
          <SendButton
            label="אישור ושליחה"
            loading={loading}
            onClick={onSubmit}
            light={true}
          />
          <Button
            loading={loading}
            onClick={onDownload}
            disabled={!selectedFile}
          >
            הורדה
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default TransferFile;